import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldAlert, RefreshCw, Loader2, CheckCircle2, AlertCircle, Search,
  Send, Building2, Mail, Clock, UserMinus, ChevronRight, Filter,
  CheckSquare, Square, Activity, TrendingUp, Info, Download,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const ACTIVATION_LABEL = {
  manual_temp_password: 'Manual temp password (legacy)',
  imported_no_invite: 'Imported · invite never sent',
  invite_sent: 'Invite sent · not activated',
};

const StatCard = ({ label, value, color, icon: Icon, testId }) => (
  <div className={`rounded-sm p-4 ring-1 ${color}`} data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold text-slate-800 leading-none">{value ?? '—'}</p>
        <p className="text-[10.5px] uppercase tracking-wide text-slate-500 font-semibold mt-1.5">{label}</p>
      </div>
      {Icon && <Icon className="w-5 h-5 text-slate-300" />}
    </div>
  </div>
);

const MemberActivationAuditPage = () => {
  const [scanning, setScanning] = useState(false);
  const [data, setData] = useState(null); // { summary, flagged }
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [activationFilter, setActivationFilter] = useState('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // post-action result

  useBodyScrollLock(confirmOpen);

  const runScan = async () => {
    setScanning(true);
    setSelected(new Set());
    setResult(null);
    try {
      const r = await axios.get(`${API}/admin/activation-audit/scan`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => { runScan();   }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.flagged.filter((u) => {
      if (activationFilter !== 'all' && u.activation_status !== activationFilter) return false;
      if (!q) return true;
      return [u.name, u.email, u.company].filter(Boolean).some((s) => s.toLowerCase().includes(q));
    });
  }, [data, search, activationFilter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      const next = new Set(selected);
      filtered.forEach((u) => next.delete(u.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filtered.forEach((u) => next.add(u.id));
      setSelected(next);
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const runReset = async () => {
    if (selected.size === 0) { toast.error('Select at least one user'); return; }
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/admin/activation-audit/reset`,
        { user_ids: Array.from(selected) },
        { withCredentials: true }
      );
      setResult(r.data);
      setConfirmOpen(false);
      const c = r.data.reset_count || 0;
      const s = (r.data.skipped_real_active || 0) + (r.data.skipped_recent_invite || 0);
      toast.success(`${c} reset · ${s} skipped`);
      // Refresh scan to update list
      runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setRunning(false);
    }
  };

  const downloadReport = () => {
    if (!data) return;
    const rows = [['Name', 'Email', 'Company', 'Status', 'Last Login', 'Last Seen', 'Invite Sent', 'Activation Status', 'Recommended Action']];
    data.flagged.forEach((u) => rows.push([
      u.name || '', u.email || '', u.company || '', u.current_status || '',
      u.last_login_at || 'never', u.last_seen_at || 'never',
      u.invite_sent_at || 'never', u.activation_status || '',
      u.recommended_action || '',
    ]));
    const csv = rows.map((r) => r.map((c) => {
      const v = String(c ?? '');
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activation-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="member-activation-audit">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-[#E63946] mb-1.5">Cleanup Tool · Membership</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#002249] tracking-tight">Member Activation Cleanup</h1>
          <p className="text-[13.5px] text-slate-500 mt-1.5 max-w-2xl leading-relaxed">
            Find members marked Active who have <strong>never actually logged in</strong>. Single source of
            truth: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[12px] font-mono">last_login_at</code>.
            Reset them back to Pending Activation and resend a fresh invite so engagement metrics become accurate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <Button variant="outline" onClick={downloadReport} className="rounded-sm" data-testid="audit-download-report">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
          )}
          <Button onClick={runScan} disabled={scanning} className="rounded-sm bg-[#002249] hover:bg-[#001833] text-white" data-testid="audit-rescan">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {scanning ? 'Scanning…' : 'Re-scan'}
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="mb-6 flex items-start gap-3 p-4 rounded-sm bg-sky-50 border border-sky-200" data-testid="audit-info-banner">
        <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
        <div className="text-[13px] text-sky-900 leading-relaxed">
          <p className="font-bold mb-1">How this works</p>
          <p>
            We scan every <strong>member</strong> account. The single rule is:{' '}
            <code className="bg-sky-100 px-1 py-0.5 rounded text-[12px]">last_login_at</code>{' '}
            is null → user has never logged in → <strong>flagged</strong>.
          </p>
          <p className="mt-1">
            Users who logged in at least once are <strong>real active members</strong> and are NEVER touched.
            Anyone who received a fresh invite in the last <strong>{data?.summary?.recent_invite_window_hours || 48} hours</strong>{' '}
            is also excluded so we don't roll back recent legitimate outreach. Reset is re-verified server-side at confirm time.
          </p>
        </div>
      </div>

      {/* Stats grid */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="audit-stats-grid">
          <StatCard label="Total Scanned" value={data.summary.total_members_scanned} color="bg-slate-50 ring-slate-200" icon={Activity} testId="stat-total" />
          <StatCard label="With last_login" value={data.summary.users_with_login ?? data.summary.real_active_members} color="bg-emerald-50 ring-emerald-200" icon={CheckCircle2} testId="stat-real" />
          <StatCard label="Flagged · No Login" value={data.summary.flagged_inactive_active} color="bg-amber-50 ring-amber-300" icon={ShieldAlert} testId="stat-flagged" />
          <StatCard label="Excluded · Recent Invite" value={data.summary.excluded_recent_invite} color="bg-sky-50 ring-sky-200" icon={Send} testId="stat-recent" />
          <StatCard label="Excluded · Already Pending" value={data.summary.excluded_already_pending} color="bg-violet-50 ring-violet-200" icon={Clock} testId="stat-pending" />
        </div>
      )}

      {/* Result banner (post-action) */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-sm flex items-start gap-3"
            data-testid="audit-result-banner"
          >
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-emerald-900">Reset complete</p>
              <p className="text-sm text-emerald-800 mt-0.5">
                {result.reset_count} reset · {result.skipped_real_active || 0} skipped (real login) ·{' '}
                {result.skipped_recent_invite || 0} skipped (recent invite) ·{' '}
                {result.skipped_already_pending || 0} skipped (already pending)
                {result.errors?.length ? ` · ${result.errors.length} errors` : ''}
              </p>
              {result.errors?.length > 0 && (
                <details className="mt-2 text-xs text-amber-800">
                  <summary className="cursor-pointer font-medium">Show errors</summary>
                  <ul className="mt-1 ml-4 list-disc">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
            <button onClick={() => setResult(null)} className="text-emerald-700 hover:text-emerald-900 text-xs font-medium underline">Dismiss</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters + Bulk action bar */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or company…"
            className="pl-8 rounded-sm h-9 text-sm"
            data-testid="audit-search"
          />
        </div>
        <select
          value={activationFilter}
          onChange={(e) => setActivationFilter(e.target.value)}
          className="border border-slate-200 rounded-sm h-9 px-2.5 text-[13px] text-slate-700 bg-white"
          data-testid="audit-filter-status"
        >
          <option value="all">All activation states</option>
          <option value="manual_temp_password">Manual temp password (legacy)</option>
          <option value="imported_no_invite">Imported · no invite</option>
          <option value="invite_sent">Invite sent · not activated</option>
        </select>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={selected.size === 0}
          className="rounded-sm bg-[#E63946] hover:bg-[#c02a35] text-white"
          data-testid="audit-bulk-reset"
        >
          <UserMinus className="w-4 h-4 mr-2" />
          Reset {selected.size > 0 ? `${selected.size} ` : ''}to Pending + Resend Invite
        </Button>
      </div>

      {/* Table */}
      {scanning && !data && (
        <div className="border border-slate-200 rounded-sm p-12 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400 mr-2" />
          <span className="text-sm text-slate-500">Scanning members…</span>
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className="border border-slate-200 rounded-sm p-10 text-center" data-testid="audit-empty-state">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <p className="text-base font-bold text-[#002249]">All clear</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {data.flagged.length === 0
              ? 'No active members without real platform activity. Activity reports are accurate.'
              : 'No members match the current filters.'}
          </p>
        </div>
      )}

      {data && filtered.length > 0 && (
        <div className="border border-slate-200 rounded-sm overflow-x-auto bg-white" data-testid="audit-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left w-10">
                  <button onClick={toggleAll} className="text-slate-500 hover:text-[#002249]" data-testid="audit-toggle-all">
                    {allFilteredSelected ? <CheckSquare className="w-4 h-4 text-[#002249]" /> : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th className="p-3 text-left font-medium text-slate-500">Member</th>
                <th className="p-3 text-left font-medium text-slate-500 hidden md:table-cell">Membership</th>
                <th className="p-3 text-left font-medium text-slate-500 hidden lg:table-cell">Company</th>
                <th className="p-3 text-left font-medium text-slate-500 hidden md:table-cell">Status</th>
                <th className="p-3 text-left font-medium text-slate-500">Last Login</th>
                <th className="p-3 text-left font-medium text-slate-500 hidden lg:table-cell">Invite Sent</th>
                <th className="p-3 text-left font-medium text-slate-500 hidden xl:table-cell">Recommended</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, idx) => {
                const checked = selected.has(u.id);
                return (
                  <tr key={u.id} className={`border-t border-slate-100 transition-colors ${checked ? 'bg-amber-50/40' : 'hover:bg-slate-50/60'}`} data-testid={`audit-row-${idx}`}>
                    <td className="p-3">
                      <button onClick={() => toggleOne(u.id)} className="text-slate-500 hover:text-[#002249]" data-testid={`audit-toggle-${idx}`}>
                        {checked ? <CheckSquare className="w-4 h-4 text-[#002249]" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="p-3 max-w-[260px]">
                      <p className="font-semibold text-slate-800 truncate">{u.name || <span className="italic text-slate-400">No name</span>}</p>
                      <p className="text-[11.5px] text-slate-500 truncate flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{u.email}</p>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10.5px] font-medium capitalize ${
                        u.membership_type === 'executive' ? 'bg-amber-100 text-amber-800' :
                        u.membership_type === 'premier'   ? 'bg-purple-100 text-purple-800' :
                        u.membership_type === 'essential' ? 'bg-blue-100 text-blue-800' :
                        u.membership_type === 'hybrid'    ? 'bg-slate-100 text-slate-700' :
                        'bg-slate-50 text-slate-400'
                      }`}>{u.membership_type || '—'}</span>
                    </td>
                    <td className="p-3 hidden lg:table-cell text-slate-600 max-w-[200px] truncate">
                      {u.company ? <span className="inline-flex items-center gap-1 text-blue-700"><Building2 className="w-3 h-3" />{u.company}</span> : <span className="text-slate-400">Individual</span>}
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10.5px] font-medium bg-emerald-100 text-emerald-800 capitalize">{u.current_status}</span>
                    </td>
                    <td className="p-3" data-testid={`audit-last-login-${idx}`}>
                      <span className="text-rose-600 text-[11.5px] font-bold inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                        never
                      </span>
                    </td>
                    <td className="p-3 hidden lg:table-cell text-slate-500 text-[11.5px]">{u.invite_sent_at ? fmtDate(u.invite_sent_at) : <span className="text-slate-400 italic">never</span>}</td>
                    <td className="p-3 hidden xl:table-cell text-slate-600 text-[11.5px]">
                      <span className="inline-flex items-center gap-1"><ChevronRight className="w-3 h-3" />Reset to Pending + resend invite</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-3 py-2.5 border-t border-slate-200 bg-slate-50 text-[11.5px] text-slate-500 flex items-center justify-between">
            <span>{filtered.length} flagged · {selected.size} selected</span>
            <span>{data.summary.total_members_scanned} members scanned · {data.summary.users_with_login ?? data.summary.real_active_members} with last_login</span>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      <ModalPortal>
        <AnimatePresence>
          {confirmOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={() => !running && setConfirmOpen(false)}
            >
              <motion.div
                initial={{ y: 32, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 16, opacity: 0 }}
                transition={{ type: 'spring', damping: 28, stiffness: 320 }}
                className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="audit-confirm-modal"
              >
                <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-[#002249] tracking-tight">Reset {selected.size} member{selected.size === 1 ? '' : 's'}?</h2>
                    <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">This will move them back to Pending Activation and send a fresh invite email.</p>
                  </div>
                </div>
                <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1">
                  <ul className="space-y-2 text-[13px] text-slate-700 leading-relaxed">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Status: Active → <strong>Pending Activation</strong></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Old manual temp password is invalidated</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> A fresh invitation token is generated</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Activation email is sent through the platform</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Membership tier &amp; payment status are kept intact</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Action is logged in admin activity</li>
                  </ul>
                  <div className="mt-4 p-3 bg-slate-50 rounded-sm border border-slate-200 text-[12px] text-slate-600">
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 text-amber-600" /> Defensive re-check</p>
                    <p className="mt-1">If anyone in the selection has logged in (<code className="bg-slate-100 px-1 py-0.5 rounded text-[11px] font-mono">last_login_at</code> set) between scan and confirm, they'll be skipped automatically.</p>
                  </div>
                </div>
                <div className="px-5 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/60 rounded-b-2xl flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={running} className="rounded-sm" data-testid="audit-confirm-cancel">Cancel</Button>
                  <Button onClick={runReset} disabled={running} className="rounded-sm bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="audit-confirm-run">
                    {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</> : <><Send className="w-4 h-4 mr-2" />Reset {selected.size} &amp; resend invite</>}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalPortal>
    </div>
  );
};

export default MemberActivationAuditPage;
